import { FileType } from '../../../types';
import type { TranscriptSearchResult } from '../../../sdk/client';
import { config } from '../../../../config';
import { screenAppAPIClient } from '../../../lib/baseAxios';
import { getThumbnailByTimestamp } from '../../../services/fileSystemService';

// Constants
const GEMINI_MODEL = 'gemini-2.0-flash';

// Interface for transcript data
export interface FileTranscript {
  fileId: string;
  fileName: string;
  text: string;
  createdAt?: string | Date;
}

/**
 * Call the Gemini API with the provided prompt.
 */
export const callGeminiAPI = async (prompt: string): Promise<string> => {
  try {
    console.log('Calling Gemini API with prompt length:', prompt.length);

    const response = await fetch(config.geminiProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: GEMINI_MODEL,
        contents: [
          {
            parts: [{ text: prompt }],
          },
        ],
      }),
    });

    if (!response.ok) {
      console.error('Gemini API error response:', {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      });

      // Try to get error details from the response if possible
      let errorDetails = '';
      try {
        const errorData = await response.json();
        errorDetails = JSON.stringify(errorData);
        console.error('Gemini API error details:', errorData);
      } catch {
        try {
          errorDetails = await response.text();
          console.error('Gemini API error text:', errorDetails);
        } catch {
          errorDetails = 'Could not extract error details';
        }
      }

      throw new Error(
        `Gemini API error: ${response.statusText} (${response.status}). Details: ${errorDetails}`,
      );
    }

    const data = await response.json();

    // Check if the response has the expected structure
    if (
      !data.candidates ||
      !data.candidates[0] ||
      !data.candidates[0].content ||
      !data.candidates[0].content.parts
    ) {
      console.error('Unexpected Gemini API response structure:', data);
      throw new Error('Unexpected Gemini API response structure');
    }

    const result = data.candidates[0].content.parts[0].text;
    console.log('Gemini API response received with length:', result.length);

    return result;
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    throw error;
  }
};

/**
 * Process a query with Gemini API to get optimized search keywords
 */
export const processQueryWithGemini = async (
  prompt: string,
  originalQuery: string,
): Promise<string> => {
  try {
    const formattedPrompt = prompt.replace('{query}', originalQuery);
    const result = await callGeminiAPI(formattedPrompt);
    return result.trim();
  } catch (error) {
    // Log the error with the Gemini API
    console.error('Gemini API error:', {
      error,
      originalQuery,
      timestamp: new Date().toISOString(),
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    // Fall back to original query if there's an error
    return originalQuery;
  }
};

/**
 * Search for transcripts
 */
export const searchTranscripts = async (
  teamId: string | undefined,
  folderId: string | undefined,
  processedQuery: string,
): Promise<TranscriptSearchResult[]> => {
  if (!teamId) {
    throw new Error('Team ID is required');
  }

  const searchParams = {
    teamId,
    query: processedQuery,
    ownerIds: [],
    parentIds: folderId ? [folderId] : [],
  };

  // Call the file system API with the search query
  const { data: searchResponse } =
    await screenAppAPIClient.files.searchTranscripts(searchParams);

  // Log the search response
  console.log('Assistant search response:', {
    searchResults: searchResponse.entries,
    pagination: searchResponse.pagination,
    timestamp: new Date().toISOString(),
    fileCount: searchResponse.entries?.length || 0,
  });

  return searchResponse.entries || [];
};

/**
 * Fetch transcripts for files
 */
export const fetchTranscripts = async (
  files: FileType[],
): Promise<FileTranscript[]> => {
  const transcripts: FileTranscript[] = [];

  // Deduplicate files by fileId to prevent processing the same transcript multiple times
  const uniqueFiles = Array.from(
    new Map(files.map((file) => [file._id, file])).values(),
  );

  console.log(
    `Deduplicating files: ${files.length} total, ${uniqueFiles.length} unique`,
  );

  // Get up to 5 files that have transcript URLs
  const filesWithTranscripts = uniqueFiles
    .filter((file) => file.textData?.transcriptUrl)
    .slice(0, 5);

  if (filesWithTranscripts.length === 0) {
    return transcripts;
  }

  try {
    await Promise.all(
      filesWithTranscripts.map(async (file) => {
        if (!file.textData?.transcriptUrl) return;

        try {
          const response = await fetch(file.textData.transcriptUrl);
          if (!response.ok) {
            console.error(
              `Failed to fetch transcript for file ${file._id}: ${response.statusText}`,
            );
            return;
          }

          const text = await response.text();

          // Parse the transcript JSON
          try {
            const parsedTranscript = JSON.parse(text);
            console.log(
              `Raw transcript for file ${file._id}:`,
              parsedTranscript,
            );

            // Store the full, raw transcript for better context
            transcripts.push({
              fileId: file._id,
              fileName: file.name,
              text,
              createdAt: file.createdAt,
            });
          } catch (parseError) {
            console.error(
              `Failed to parse transcript for file ${file._id}:`,
              parseError,
            );
          }
        } catch (fetchError) {
          console.error(
            `Failed to fetch transcript for file ${file._id}:`,
            fetchError,
          );
        }
      }),
    );

    return transcripts;
  } catch (error) {
    console.error('Error fetching transcripts:', error);
    return transcripts;
  }
};

/**
 * Fetch thumbnail for a specific timestamp in a video
 */
export const fetchThumbnail = async (
  fileId: string,
  timestamp?: string,
): Promise<string | null> => {
  try {
    if (!fileId) return null;

    const thumbnailUrl = await getThumbnailByTimestamp({
      fileId,
      timestamp: timestamp ? parseInt(timestamp, 10) : 0,
    });
    return thumbnailUrl;
  } catch (error) {
    console.error('Error fetching thumbnail:', error);
    return null;
  }
};

/**
 * Generate an answer from provided transcripts using Gemini API
 */
export const generateAnswerWithGemini = async (
  prompt: string,
  originalQuery: string,
  transcriptXML: string,
  userContext: {
    userName: string;
    userRole: string;
    teamName: string;
    customVocabulary: string;
    defaultLanguage: string;
  },
): Promise<string> => {
  try {
    // Create the user context by replacing variables in the prompt
    let formattedPrompt = prompt
      .replace('{query}', originalQuery)
      .replace('{transcripts}', transcriptXML);

    // Replace user context variables with actual values
    formattedPrompt = formattedPrompt
      .replace('{user.name}', userContext.userName || 'User')
      .replace('{user.role}', userContext.userRole || 'User')
      .replace('{team.name}', userContext.teamName || 'Personal')
      .replace(
        '{team.fileOptions.customVocabulary}',
        userContext.customVocabulary || 'None',
      )
      .replace(
        '{recorder.otherSettings.defaultTranscriptionLanguage}',
        userContext.defaultLanguage || 'Default',
      );

    // Call Gemini API
    const result = await callGeminiAPI(formattedPrompt);
    return result.trim();
  } catch (error) {
    console.error('Error generating answer:', error);
    throw error;
  }
};
